import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import {
  HiXMark,
  HiDocumentText,
  HiUser,
  HiEnvelope,
  HiCalendar,
  HiShieldCheck,
  HiCheckCircle,
  HiExclamationTriangle,
} from 'react-icons/hi2';
import { toast } from 'react-hot-toast';

const ReportSubmissionForm = ({ isOpen, onClose, client, currentUser, userRole }) => {
  const [reportContent, setReportContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const canSubmitReport = ['admin', 'moderator', 'coach'].includes(userRole);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!canSubmitReport) {
      setError('You do not have permission to submit reports');
      return;
    }

    if (!reportContent.trim()) {
      setError('Please enter report content');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const reportData = {
        submittedByEmail: currentUser.email,
        submittedByRole: userRole,
        submittedByName:
          currentUser.displayName ||
          `${currentUser.firstname || ''} ${currentUser.lastname || ''}`.trim() ||
          'Unknown',
        timestamp: serverTimestamp(),
        reportContent: reportContent.trim(),
        clientEmail: client.email,
        clientName: `${client.firstname || ''} ${client.lastname || ''}`.trim(),
        clientId: client.id,
      };

      const reportsRef = collection(firestore, 'clients', client.id, 'reports');
      await addDoc(reportsRef, reportData);

      setSuccess(true);
      setReportContent('');

      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReportContent('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative w-[92%] max-w-2xl rounded-xl border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5 sm:w-full"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600">
                  <HiDocumentText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-black">Submit Private Report</h3>
                  <p className="text-sm text-gray-500">
                    Report for {client.firstname || ''} {client.lastname || ''}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="rounded-full p-2 text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed"
              >
                <HiXMark className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 md:p-6">
              {!canSubmitReport ? (
                <div className="py-8 text-center">
                  <HiExclamationTriangle className="mx-auto h-12 w-12 text-red-500" />
                  <h3 className="mt-4 text-lg font-semibold text-black">Access Denied</h3>
                  <p className="mt-2 text-gray-600">
                    You do not have permission to submit reports. Only administrators, business
                    advisors, and coaches can submit reports.
                  </p>
                </div>
              ) : success ? (
                <div className="py-8 text-center">
                  <HiCheckCircle className="mx-auto h-12 w-12 text-green-500" />
                  <h3 className="mt-4 text-lg font-semibold text-black">
                    Report Submitted Successfully
                  </h3>
                  <p className="mt-2 text-gray-600">Your private report has been saved securely.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                  <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-3 md:p-4">
                    <h4 className="mb-3 flex items-center gap-2 text-xs font-semibold text-gray-900 md:text-sm">
                      <HiShieldCheck className="h-4 w-4 text-blue-600" />
                      Report Information
                    </h4>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="flex items-center gap-2">
                        <HiUser className="h-4 w-4 text-gray-500" />
                        <span className="text-xs text-gray-600 md:text-sm">
                          <span className="font-medium">Reporter:</span> {currentUser.email}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <HiEnvelope className="h-4 w-4 text-gray-500" />
                        <span className="text-xs text-gray-600 md:text-sm">
                          <span className="font-medium">Role:</span> {userRole}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <HiCalendar className="h-4 w-4 text-gray-500" />
                        <span className="text-xs text-gray-600 md:text-sm">
                          <span className="font-medium">Date:</span>{' '}
                          {new Date().toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <HiUser className="h-4 w-4 text-gray-500" />
                        <span className="text-xs text-gray-600 md:text-sm">
                          <span className="font-medium">Client:</span> {client.firstname || ''}{' '}
                          {client.lastname || ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-700 md:text-sm">
                      Report Content *
                    </label>
                    <textarea
                      value={reportContent}
                      onChange={e => setReportContent(e.target.value)}
                      required
                      rows={8}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter your detailed report about this client. This information will be kept private and only visible to administrators, business advisors, and coaches."
                    />
                    <p className="mt-1 text-[10px] text-gray-500 md:text-xs">
                      This report is confidential and will not be visible to the client.
                    </p>
                  </div>

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <div className="flex items-center gap-2">
                        <HiExclamationTriangle className="h-4 w-4 text-red-500" />
                        <span className="text-sm text-red-700">{error}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={loading}
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed md:px-4"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !reportContent.trim()}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 md:px-4"
                    >
                      {loading ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <HiDocumentText className="h-4 w-4" />
                          Submit Report
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportSubmissionForm;
